import { apiRequest } from "./api";
import type { components } from "./api-types";

/**
 * v2.2 P4: calls, texts and voicemails that already happened — read, play, and link. Nothing here can
 * place a call; "Voice" opens Google Voice's own dialer page for Sam to press call himself (Rule 0).
 */
type S = components["schemas"];
export type TelephonyEventRow = S["TelephonyEventRow"];
export type TelephonyEventPage = S["TelephonyEventPage"];
export type TelephonyLinkTarget = S["TelephonyLinkTarget"];
export type TelephonyParseFailure = S["TelephonyParseFailure"];
export type TelephonyUnmatchedGroup = S["TelephonyUnmatchedGroup"];
export type TelephonyIgnoredNumber = S["TelephonyIgnoredNumber"];
export type TelephonyPhoneImport = S["TelephonyPhoneImport"];
export type TelephonySuggestionGroup = S["TelephonySuggestionGroup"];
export type CvUniqueSuggestion = S["CvUniqueSuggestion"];
export type CvUniqueApplied = S["CvUniqueApplied"];
export type CvPhoneAttachment = S["CvPhoneAttachment"];

const BASE = "/api/v1/telephony";

export const telephony = {
  events: (query: { lead_id?: string; deal_id?: string; unmatched?: boolean }) => {
    const p = new URLSearchParams();
    if (query.lead_id) p.set("lead_id", query.lead_id);
    if (query.deal_id) p.set("deal_id", query.deal_id);
    if (query.unmatched) p.set("unmatched", "true");
    return apiRequest<TelephonyEventPage>(`${BASE}/events?${p.toString()}`);
  },
  recording: (id: string) => apiRequest<{ url: string }>(`${BASE}/events/${id}/recording`),
  link: (id: string, body: { lead_id?: string; deal_id?: string }) =>
    apiRequest<TelephonyEventRow>(`${BASE}/events/${id}/link`, { method: "POST", body: JSON.stringify(body) }),
  createLead: (id: string) =>
    apiRequest<{ lead_id: string }>(`${BASE}/events/${id}/create-lead`, { method: "POST" }),
  // v2.4 P1.6: payloads gv_sync could not classify; the owner resolves one once it is handled.
  parseFailures: () => apiRequest<TelephonyParseFailure[]>(`${BASE}/parse-failures`),
  resolveParseFailure: (id: string) =>
    apiRequest<TelephonyParseFailure>(`${BASE}/parse-failures/${id}/resolve`, { method: "POST" }),
  linkTargets: (q: string) =>
    apiRequest<TelephonyLinkTarget[]>(`${BASE}/link-targets?q=${encodeURIComponent(q)}`),
  // v2.5 P2.3: the Unmatched bucket by number, and the numbers nobody needs to work.
  unmatchedGroups: () => apiRequest<TelephonyUnmatchedGroup[]>(`${BASE}/unmatched/groups`),
  // v2.7 P3.1: per number, the deals the owner worked within ±2 h of its calls.
  suggestions: () => apiRequest<TelephonySuggestionGroup[]>(`${BASE}/unmatched/suggestions`),
  ignoredNumbers: () => apiRequest<TelephonyIgnoredNumber[]>(`${BASE}/ignored-numbers`),
  ignoreNumber: (body: { phone_e164: string; reason: string; note?: string }) =>
    apiRequest<TelephonyIgnoredNumber>(`${BASE}/ignored-numbers`, { method: "POST", body: JSON.stringify(body) }),
  // v2.5 P2.4: phones from a CSV onto existing deals (by APN/external_key) or leads; dry run first.
  importPhones: (file: File, opts: { target: "deals" | "leads"; boardId?: string; dryRun: boolean }) => {
    const form = new FormData();
    form.set("file", file);
    form.set("target", opts.target);
    if (opts.boardId) form.set("board_id", opts.boardId);
    form.set("dry_run", opts.dryRun ? "true" : "false");
    return apiRequest<TelephonyPhoneImport>(`${BASE}/import-phones`, { method: "POST", body: form });
  },
  // v2.9 P4.2 (D-129): accept all unique suggestions — preview, then Sam's Apply.
  acceptUniquePreview: () => apiRequest<CvUniqueSuggestion[]>(`${BASE}/unmatched/accept-unique`),
  acceptUniqueApply: () =>
    apiRequest<CvUniqueApplied>(`${BASE}/unmatched/accept-unique`, { method: "POST" }),
  // v2.9 (D-125): numbers attached in the last 7 days, and their undo.
  attachments: () => apiRequest<CvPhoneAttachment[]>(`${BASE}/attachments`),
  undoAttachment: (id: string) =>
    apiRequest<CvPhoneAttachment>(`${BASE}/attachments/${id}/undo`, { method: "POST" }),
  restoreNumber: (id: string) =>
    apiRequest<TelephonyIgnoredNumber>(`${BASE}/ignored-numbers/${id}/restore`, { method: "POST" }),
};

// v2.10 P1.2 (D-133): the Voice links live in ./voice (hello@'s account, one reused window). Re-exported
// here so the v2.2–v2.9 imports keep working.
export { voiceCallsUrl, voiceDialUrl } from "./voice";

export const KIND_LABEL: Record<string, string> = {
  call_in: "Inbound call",
  call_out: "Outbound call",
  missed: "Missed call",
  voicemail: "Voicemail",
  sms_in: "Text in",
  sms_out: "Text out",
};

/** `2m 05s`, `45s`, or "—" for a text / an unanswered call. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds <= 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}
